/*!

=========================================================
* Argon Dashboard PRO React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// react library for routing
import { useLocation, NavLink as NavLinkRRD, Link } from "react-router-dom";
// nodejs library that concatenates classes
import classnames from "classnames";
// nodejs library to set properties for components
import { PropTypes } from "prop-types";
// react library that creates nice scrollbar on windows devices
// reactstrap components
import {
    Collapse,
    NavbarBrand,
    Navbar,
    NavItem,
    NavLink,
    Nav,
} from "reactstrap";
import { useAuth } from "react-oidc-context";
import { getBootstrapIcon } from "../../utilities/iconMapper"; 
import "./Sidebar.css";
import { getSidebarSections } from "../../utilities/sidebarHelpers";

import { SidebarAccordion } from "./SidebarAccordion";

import dcgLogo from "../../assets/img/brand/dcg_logo_header.png";

function Sidebar(
    {   toggleSidenav = () => { },
        sidenavOpen = false,
        routes = [{}],
        logo,
        rtlActive = false,
        openSectionId,
        setOpenSectionId = () => { },
        handleExpandFromCollapsed = () => { }
    }) {
    const [state, setState] = React.useState({});
    const location = useLocation();
    const auth = useAuth();

    React.useEffect(() => {
        setState(getCollapseStates(routes));
        // eslint-disable-next-line
    }, []);
    // verifies if routeName is the one active (in browser input)
    const activeRoute = (routeName) => {
        return location.pathname.indexOf(routeName) > -1 ? "active" : "";
    };
    // makes the sidenav normal on hover (actually when mouse enters on it)
    const onMouseEnterSidenav = () => {
        
    };
    // makes the sidenav mini on hover (actually when mouse leaves from it)
    const onMouseLeaveSidenav = () => {
        if (!document.body.classList.contains("g-sidenav-pinned")) {
            document.body.classList.remove("g-sidenav-show");
        }
    };
    // this creates the intial state of this component based on the collapse routes
    // that it gets through routes
    const getCollapseStates = (routes) => {
        let initialState = {};
        routes.map((prop, key) => {
            if (prop.collapse) {
                initialState = {
                    [prop.state]: getCollapseInitialState(prop.views),
                    ...getCollapseStates(prop.views),
                    ...initialState,
                };
            }
            return null;
        });
        return initialState;
    };
    // this verifies if any of the collapses should be default opened on a rerender of this component
    const getCollapseInitialState = (routes) => {
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
                return true;
            } else if (location.pathname.indexOf(routes[i].path) !== -1) {
                return true;
            }
        }
        return false;
    };
    // this is used on mobile devices, when a user navigates
    // the sidebar will autoclose
    const closeSidenav = () => {
        if (window.innerWidth < 1200) {
            toggleSidenav();
        }
    };
    // this function creates the links and collapses that appear in the sidebar (left menu)
    const createLinks = (routes) => {
        return routes.map((prop, key) => {
            if (prop.redirect) {
                return null;
            }
            if (prop.collapse) {
                var st = {};
                st[prop["state"]] = !state[prop.state];
                return (
                    <NavItem key={key} className="pl-3">
                        <NavLink
                            href="#"
                            data-toggle="collapse"
                            aria-expanded={state[prop.state]}
                            className={classnames({
                                active: getCollapseInitialState(prop.views),
                            })}
                            onClick={(e) => {
                                e.preventDefault();
                                setState(st);
                            }}
                        >
                            {getNavlinkContext(prop)}
                        </NavLink>
                        
                        <Collapse isOpen={state[prop.state]}>
                            <Nav className="nav-sm flex-column">
                                {createLinks(prop.views)}
                            </Nav>
                        </Collapse>
                    </NavItem>
                );
            }
            return (
                <NavItem className={activeRoute(prop.layout + prop.path)} key={key}>
                    {getNavItems(prop)}
                </NavItem>
            );
        });
    };

    const getNavItems = (prop) => isInternalUrl(prop) ? getInternalLink(prop) : getExternalLink(prop);

    const getInternalLink = (prop) => {
        return (
            <NavLink
                to={prop.layout + prop.path}
                onClick={closeSidenav}
                tag={NavLinkRRD}
            >
                {getNavlinkContext(prop)}
            </NavLink>
        );
    };

    const getExternalLink = (prop) => {
        //Custom property check to open external link in existing tab
        if (prop.externalInWindow === true) {
            return (
                <NavLink
                    href={prop.path}
                    onClick={closeSidenav}
                >
                    {getNavlinkContext(prop)}
                </NavLink>
            );
        }
        //Undefined & explicitly false prop will open in new tab
        else {
            return (
                <NavLink
                    href={prop.path}
                    onClick={closeSidenav}
                    target="_blank"
                >
                    {getNavlinkContext(prop)}
                </NavLink>
            );
        }
        
    };

    const isInternalUrl = (prop) => !prop.path.includes("https");

    const getNavlinkContext = (prop) => {
        if (prop.icon) {
            const Icon = getBootstrapIcon(prop.icon);
            return (
                <>
                    <Icon className="me-2" />
                    <span className="nav-link-text pl-2">{prop.name}</span>
                </>
            );
        }

        if (prop.miniName) {
            return (
                <>
                    <span className="sidenav-mini-icon">{prop.miniName}</span>
                    <span className="sidenav-normal">{prop.name}</span>
                </>
            );
        }

        return prop.name;
    };


    let navbarBrandProps;
    if (logo && logo.innerLink) {
        navbarBrandProps = {
            to: logo.innerLink,
            tag: Link,
        };
    } else if (logo && logo.outterLink) {
        navbarBrandProps = {
            href: logo.outterLink,
            target: "_blank",
        };
    }

    const dcgLogoObj = {
        innerLink: "/",
        imgSrc: dcgLogo,
        imgAlt: "...",
    }

    const sidebarSections = getSidebarSections(routes, { dcg: dcgLogoObj });
    const navContent = (
        <div>
            <div className="sidenav-header d-flex align-items-center">
                {logo ? (
                    <NavbarBrand {...navbarBrandProps}>
                        <img
                            alt={logo.imgAlt}
                            className="navbar-brand-img"
                            src={logo.imgSrc}
                        />
                    </NavbarBrand>
                ) : null}
                <div className="ml-auto">
                    <div
                        className={classnames("sidenav-toggler d-xl-block mr-1", {
                            active: sidenavOpen,
                        })}
                        onClick={toggleSidenav}
                    >
                        <div className="sidenav-toggler-inner">
                            <i className="sidenav-toggler-line" />
                            <i className="sidenav-toggler-line" />
                            <i className="sidenav-toggler-line" />
                        </div>
                    </div>
                </div>
            </div>
            {(auth?.user?.access_token) && (
                
                <div className="sidebar-container">
                    <Collapse navbar isOpen={sidenavOpen} className="sidebar-collapse">
                        <div className="navbar-inner">
                            <Nav navbar className="nav-main">
                                {createLinks(routes.filter((r) => r.icon))}
                            </Nav>
                        </div>
                        {/* Accordion here */}
                        <SidebarAccordion
                            sections={sidebarSections}
                            createLinks={createLinks}
                            sidenavOpen={sidenavOpen}
                            openSectionId={openSectionId}
                            setOpenSectionId={setOpenSectionId}
                            onExpandFromCollapsed={handleExpandFromCollapsed}
                            toggleSidenav={toggleSidenav}
                        />

                        
                        
                    </Collapse>
                </div>

            )}
        </div>
    );
    return (
        <Navbar
            style={{ overflowX: "hidden", overflowY: "hidden" }}
            className={
                `sidenav navbar-vertical navbar-expand-xs navbar-light bg-white ${rtlActive ? "" : "fixed-left"
                } ${!sidenavOpen ? "collapsed" : ""}`
            }
            onMouseEnter={onMouseEnterSidenav}
            onMouseLeave={onMouseLeaveSidenav}
        >
            {navContent}
        </Navbar>

    );
}

Sidebar.propTypes = {
    // function used to make sidenav mini or normal
    toggleSidenav: PropTypes.func,
    // prop to know if the sidenav is mini or normal
    sidenavOpen: PropTypes.bool,
    // links that will be displayed inside the component
    routes: PropTypes.arrayOf(PropTypes.object),
    // logo
    logo: PropTypes.shape({
        // innerLink is for links that will direct the user within the app
        // it will be rendered as <Link to="...">...</Link> tag
        innerLink: PropTypes.string,
        // outterLink is for links that will direct the user outside the app
        // it will be rendered as simple <a href="...">...</a> tag
        outterLink: PropTypes.string,
        // the image src of the logo
        imgSrc: PropTypes.string.isRequired,
        // the alt for the img
        imgAlt: PropTypes.string.isRequired,
    }),
    // rtl active, this will make the sidebar to stay on the right side
    rtlActive: PropTypes.bool,
};

export default Sidebar;
